import { IconActionChevronLeft16, IconActionChevronRight16 } from '@cian/ui-kit-design-tokens/icons';
import * as React from 'react';
import { useSelector } from 'react-redux';
import styles from './RecentlyWatchedOffers.css';
import { OfferCard } from './components/OfferCard';
import { userProfileRecentlyWatchedOffersSelector } from '../../../../../selectors/userProfile';
import { AboutTabSection } from '../AboutTabSection';
import { RoundButton } from '@cian/ui-kit';

const SCROLL_STEP_RATIO = 0.8;

export const RecentlyWatchedOffers = () => {
  const recentlyWatchedOffers = useSelector(userProfileRecentlyWatchedOffersSelector);

  const title = recentlyWatchedOffers?.title ?? '';
  const offers = recentlyWatchedOffers?.offers ?? [];

  const trackRef = React.useRef<HTMLDivElement>(null);

  const [canScrollPrev, setCanScrollPrev] = React.useState(false);
  const [canScrollNext, setCanScrollNext] = React.useState(false);

  const updateScrollState = React.useCallback(() => {
    const node = trackRef.current;

    if (!node) {
      return;
    }

    const first = node.firstElementChild;
    const last = node.lastElementChild;

    if (!first || !last) {
      setCanScrollPrev(false);
      setCanScrollNext(false);

      return;
    }

    const containerRect = node.getBoundingClientRect();
    const firstRect = first.getBoundingClientRect();
    const lastRect = last.getBoundingClientRect();

    setCanScrollPrev(firstRect.left < containerRect.left - 1);
    setCanScrollNext(lastRect.right > containerRect.right + 1);
  }, []);

  React.useEffect(() => {
    updateScrollState();

    const node = trackRef.current;

    if (!node) {
      return undefined;
    }

    node.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);

    return () => {
      node.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [updateScrollState, offers.length]);

  const handlePrevClick = React.useCallback(() => {
    const node = trackRef.current;

    if (!node) {
      return;
    }

    node.scrollBy({ left: -Math.round(node.clientWidth * SCROLL_STEP_RATIO), behavior: 'smooth' });
  }, []);

  const handleNextClick = React.useCallback(() => {
    const node = trackRef.current;

    if (!node) {
      return;
    }

    node.scrollBy({ left: Math.round(node.clientWidth * SCROLL_STEP_RATIO), behavior: 'smooth' });
  }, []);

  if (!offers.length) {
    return null;
  }

  return (
    <AboutTabSection title={title} testId="RecentlyWatchedOffers">
      <div className={styles['slider']}>
        <div className={styles['slides']} ref={trackRef}>
          {offers.map(offer => (
            <OfferCard key={offer.id} offer={offer} />
          ))}
        </div>
        {canScrollPrev && (
          <div className={styles['prev_button']}>
            <RoundButton
              theme="light"
              size="M"
              icon={<IconActionChevronLeft16 color="icon-primary-default" />}
              title=""
              onClick={handlePrevClick}
              data-testid="RecentlyWatchedOffersButtonPrev"
            />
          </div>
        )}
        {canScrollNext && (
          <div className={styles['next_button']}>
            <RoundButton
              theme="light"
              size="M"
              icon={<IconActionChevronRight16 color="icon-primary-default" />}
              title=""
              onClick={handleNextClick}
              data-testid="RecentlyWatchedOffersButtonNext"
            />
          </div>
        )}
      </div>
    </AboutTabSection>
  );
};
